import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useConfigStore } from '../state/store/config/useConfigStore';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { ConfigurationSettings } from '/models/configuration-settings';
import { AuthStore } from '/state/store/auth/authStore';

interface Https {
  get<T>(url: string, blocksKey: string, headers?: HeadersInit): Promise<T>;

  post<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers?: HeadersInit,
    noContentType?: boolean
  ): Promise<T>;

  put<T>(url: string, blocksKey: string, body: BodyInit, headers?: HeadersInit): Promise<T>;

  delete<T>(url: string, blocksKey: string, headers?: HeadersInit): Promise<T>;

  stream(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers?: HeadersInit
  ): Promise<ReadableStreamDefaultReader<Uint8Array>>;

  request<T>(
    url: string,
    blocksKey: string,
    options: RequestOptions,
    noContentType?: boolean,
    hasRetriedAuth?: boolean
  ): Promise<T>;

  createHeaders(blocksKey: string, headers: any, noContentType?: boolean): Headers;
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
}

interface RequestQueue<T> {
  url: string;
  blocksKey: string;
  requestOption: RequestOptions;
  noContentType?: boolean;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

let isRefreshing = false;
let requestQueue: RequestQueue<unknown>[] = [];

// const prepareUrl = (url: string): string => {
//   if (url.startsWith('https://api.selise.ai/taskforce')) {
//     return url.replace(
//       'https://api.selise.ai/taskforce',
//       'https://taskforce.cloudwhimsy.com/taskforce'
//     );  
//   }
//   return url;
// };

// https://taskforce.cloudwhimsy.com/taskforce/api/collaboration/conversations
export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
  }
}

async function refreshAccessToken(endpoint: string, method: string): Promise<void> {
  if (isRefreshing) return;
  isRefreshing = true;

  try {
    const authStore = useAuthStore.getState();
    const config = useConfigStore.getState().configurationSettings;

    if (!config) {
      console.error('[Auth Error] Configuration settings not found');
      throw new Error('Configuration settings not found');
    }

    const isOIDCLogin = authStore.isOIDCLogin;
    if (!isOIDCLogin) {
      const data = await refreshTokenNonOIDCFlow(config, authStore);
      console.log('data', data);
      return;
    }

    /// OIDC token refresh
    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', authStore.refreshToken || '');
    
    const clientId = import.meta.env.VITE_OIDC_CLIENT_ID;
    if (clientId) {
      formData.append('client_id', clientId);
    }

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}/iam/v4/oidc/token`;

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Blocks-Key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    if (data.access_token && data.refresh_token) {
      authStore.setAccessToken(data.access_token);
      authStore.setRefreshToken(data.refresh_token);
    }

    while (requestQueue.length > 0) {
      const { url, blocksKey, requestOption, noContentType, resolve, reject } = requestQueue.shift()!;
      clients.request(url, blocksKey, requestOption, noContentType).then(resolve).catch(reject);
    }
  } catch (_error) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  } finally {
    isRefreshing = false;
    requestQueue = [];
  }
}

async function refreshTokenNonOIDCFlow(config: ConfigurationSettings, authStore: AuthStore): Promise<void> {
  try {

    if (!config) {
      console.error('[Auth Error] Configuration settings not found');
      throw new Error('Configuration settings not found');
    }

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.authentication.refreshToken}`;
    
    const payload = {
      refresh_token: "",
    };

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
        'X-Blocks-Key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    if (data.access_token && data.refresh_token) {
      authStore.setAccessToken(data.access_token);
      authStore.setRefreshToken(data.refresh_token);
    }

    console.log('data', data);

    while (requestQueue.length > 0) {
      const { url, blocksKey, requestOption, noContentType, resolve, reject } = requestQueue.shift()!;
      clients.request(url, blocksKey, requestOption, noContentType).then(resolve).catch(reject);
    }
  } catch (_error) {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  } finally {
    isRefreshing = false;
    requestQueue = [];
  }
}

/** Refresh the Blocks/Genesis JWT after a 401 (shared by Hermes gateway stream, etc.). */
export async function refreshBlocksAccessToken(endpoint: string, method: string): Promise<void> {
  return refreshAccessToken(endpoint, method);
}

async function streamRequest(
  client: Https,
  url: string,
  blocksKey: string,
  body: BodyInit,
  headers: HeadersInit,
  hasRetriedAuth: boolean
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const fullUrl = url.startsWith('http') ? url : url;
  const requestHeaders = client.createHeaders(blocksKey, headers);

  const config: RequestInit = {
    method: 'POST',
    headers: requestHeaders,
    body,
    referrerPolicy: 'no-referrer',
    credentials: 'include',
  };

  let response: Response;
  try {
    response = await fetch(fullUrl, config);

    if (response.status === 401) {
      if (hasRetriedAuth) {
        trackEvent(EVENTS.ERROR('Unauthorized', {
          endpoint: url,
          method: 'POST',
          responseStatus: response.status,
        }));
        throw new HttpError(401, { error: 'Unauthorized' });
      }

      await refreshAccessToken(url, 'POST');
      return streamRequest(client, url, blocksKey, body, headers, true);
    }

    if (!response.ok || !response.body) {
      // Read the body the way the JSON path does, so a server message -- the usage
      // gate's 402 detail, say -- survives to the caller instead of being replaced.
      let err: Record<string, unknown> = { error: 'Streaming failed' };
      try {
        const parsed = await response.json();
        if (parsed && typeof parsed === 'object') err = parsed;
      } catch {
        // Not JSON, keep the generic message.
      }
      trackEvent(EVENTS.ERROR(err, {
        endpoint: url,
        method: 'POST',
        responseStatus: response.status,
      }));
      throw new HttpError(response.status, err);
    }
  } catch (err) {
    if (err instanceof HttpError) throw err;
    throw new HttpError(500, { error: 'Network error' });
  }

  return response.body.getReader();
}

export const clients: Https = {
  async get<T>(url: string, blocksKey: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'GET', headers });
  },
  

  async post<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers: HeadersInit = {},
    noContentType = false
  ): Promise<T> {
    return this.request<T>(
      url,
      blocksKey,
      {
        method: 'POST',
        headers,
        body,
      },
      noContentType
    );
  },

  async put<T>(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers: HeadersInit = {}
  ): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'PUT', headers, body });
  },

  
  async delete<T>(url: string, blocksKey: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, blocksKey, { method: 'DELETE', headers });
  },

  async stream(
    url: string,
    blocksKey: string,
    body: BodyInit,
    headers: HeadersInit = {}
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    return streamRequest(this, url, blocksKey, body, headers, false);
  },

  async request<T>(
    url: string,
    blocksKey: string,
    { method, headers = {}, body }: RequestOptions,
    noContentType = false,
    hasRetriedAuth = false
  ): Promise<T> {
    // Short-circuit requests built from empty config paths (seliseai features
    // intentionally hidden in the Blocks ecosystem — their path strings are '').
    // The URL ends up as just the host (e.g. `https://dev-blocksapi.selise.ai/`
    // or `https://.../?param=...`). Returning an empty object prevents both
    // the CORS error spray and the "Failed to fetch" uncaught exceptions.
    try {
      const parsed = new URL(url);
      if (!parsed.pathname || parsed.pathname === '/') {
        return {} as T;
      }
    } catch {
      // If URL parsing fails, fall through to the fetch (will error naturally).
    }

    const requestHeaders = this.createHeaders(blocksKey, headers, noContentType);
    // @todo need to remove this before merging into the dev
    // url = prepareUrl(url);

    const config: RequestInit = {
      method,
      credentials: 'include',
      headers: requestHeaders,
    };

    if (body) {
      config.body = body;
    }

    const response = await fetch(url, config);

    if (response.ok) {
      const contentType = response.headers.get('Content-Type') || '';
      if (
        contentType.includes('audio/') ||
        contentType.includes('image/') ||
        contentType.includes('application/pdf') ||
        contentType.includes('application/octet-stream') ||
        contentType.includes('application/zip')
      ) {
        return response as T;
      }
      if (contentType.includes('text/event-stream')) {
        return response as T;
      }
      const responseText = await response.text();
      const finalResponseText = responseText.trim() === '' ? '{}' : responseText;
      try {
        return JSON.parse(finalResponseText) as T;
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new HttpError(response.status, {
          error: 'Invalid JSON response',
          rawResponse: finalResponseText,
        });
      }
    }

    if (response.status === 401) {
      return new Promise<T>((resolve, reject) => {
        requestQueue.push({
          url,
          blocksKey,
          requestOption: { method, headers, body },
          noContentType,
          resolve: resolve as (value: unknown | PromiseLike<unknown>) => void,
          reject,
        });
        if (!isRefreshing) refreshAccessToken(url, method);
      });
    }

     if (response.status === 403) {
      const responseText = await response.text();
      const finalResponseText = responseText.trim() === '' ? '{}' : responseText;
      let err: Record<string, unknown>;
      try {
        err = JSON.parse(finalResponseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        err = { error: 'Forbidden', rawResponse: finalResponseText };
      }
      trackEvent(EVENTS.ERROR(err, {
        endpoint: url,
        method,
        responseStatus: response.status,
      }));
      throw new HttpError(response.status, err);
    }

    const err = await response.json();
    trackEvent(EVENTS.ERROR(err, {
      endpoint: url,
      method,
      responseStatus: response.status
    }));
    throw new HttpError(response.status, err);
  },

  createHeaders(blocksKey: string, headers: any, noContentType = false): Headers {
    const authToken = useAuthStore.getState().accessToken;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': blocksKey,
      ...(authToken && { Authorization: `bearer ${authToken}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const _headers = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });

    if (noContentType) _headers.delete('Content-Type');
    return _headers;
  },
};
